import * as React from "react";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import { useOrgMemberDirectory } from "@/shared/domain/orgs/hooks";

import { CommentRecord } from "../CommentRecord";

/** User IDs for system users */
const ROBOTO_PLATFORM_USER_IDS = ["support@roboto.ai", "system@roboto.ai"];

/**
 * Comment record with additional user information for display purposes.
 */
export interface UIComment extends CommentRecord {
  /** URL of the comment author's profile picture */
  avatarUrl: string | undefined;

  /** Display name of the comment author */
  creatorDisplayName?: string;

  /** Whether the comment author is a system user */
  isSystemUser: boolean;
}

/**
 * Enriches comment records with user information (avatar URL and display name)
 * from the organization roles.
 *
 * @param comments - Array of comment records to enrich
 * @returns Array of enriched comments with avatar and display name information
 */
export function useUIComments(comments: CommentRecord[]): UIComment[] {
  const memberDirectory = useOrgMemberDirectory(useCurrentOrgId());

  return React.useMemo(() => {
    // An empty directory (still loading, or no members) simply leaves the
    // enrichment fields undefined — the same result the old null-guard produced.
    return comments.map(
      (comment): UIComment => ({
        ...comment,
        avatarUrl:
          memberDirectory.get(comment.created_by)?.picture_url ?? undefined,
        creatorDisplayName: memberDirectory.get(comment.created_by)?.name,
        isSystemUser: ROBOTO_PLATFORM_USER_IDS.includes(comment.created_by),
      }),
    );
  }, [comments, memberDirectory]);
}
